import { useDispatch, useSelector } from 'react-redux';
import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import {
  getChannelAvailability,
  getIsFetchProductAvailabilityPending,
  getIsProductDataPending,
  getIsProductInventoryChecked, getIsProductSoldOut, getIsSetProduct,
  getProductDetails,
  getSelectedAddonsList, getSelectedModifiersList, getSelectedQty,
  getSetProductsSkus,
} from '../../../store/selectors/product.selectors';
import { ProductDto } from '../../../types/schema';
import { getIsCartDataPending } from '../../../store/selectors/cart.selectors';
import { getIsOnline } from '../../../store/selectors/ui.selectors';
import { triggerAddToCartAction } from './common';
import { addToCart } from '../../../store/actions/cart.actions';

// eslint-disable-next-line import/prefer-default-export
export const useAddToCartAction = () => {
  const dispatch = useDispatch();
  const wasAddedBeforeReadyRef = useRef(false);
  const [disabled, setDisabled] = useState(false);
  const selectedAddons: { outOfStock: boolean }[] = useSelector(getSelectedAddonsList);
  const productDetails: ProductDto | null = useSelector(getProductDetails);
  const checkedInventory = useSelector(getIsProductInventoryChecked);
  const soldOut = useSelector(getIsProductSoldOut);
  const selectedQty = useSelector(getSelectedQty);
  const selectedModifiersList: unknown[] = useSelector(getSelectedModifiersList);

  const isProductDataPending = useSelector(getIsProductDataPending);
  const isCartDataPending: boolean = useSelector(getIsCartDataPending);
  const isFetchProductAvailabilityPending = useSelector(getIsFetchProductAvailabilityPending);
  const isOnline = useSelector(getIsOnline);
  const channelAvailability = useSelector(getChannelAvailability);
  const isSetProduct = useSelector(getIsSetProduct);
  const setProductsSkus: string[] = useSelector(getSetProductsSkus);

  const outOfStockAddOnFound = selectedAddons.some(({ outOfStock }) => outOfStock);
  const singleProductSku = productDetails ? [productDetails.sku] : [];
  const productSkus = isSetProduct ? setProductsSkus : singleProductSku;
  const availableInChannel = productSkus.every((sku) => (
    channelAvailability?.[sku]?.channelAvailability
  ));

  const anyRequestPending = isProductDataPending
    || isCartDataPending
    || isFetchProductAvailabilityPending;

  const isReady = !anyRequestPending
    && checkedInventory
    && !soldOut
    && isOnline
    && availableInChannel
    && !outOfStockAddOnFound;

  const addToCartHandler = useCallback(() => {
    setDisabled(true);

    const addToCartAction = (
      (...args: Parameters<typeof addToCart>) => dispatch(addToCart(...args))
    );

    triggerAddToCartAction({
      disabled: !isReady,
      productDetails,
      checkedInventory,
      soldOut,
      addToCartAction,
      selectedAddons,
      selectedQty,
      selectedModifiersList,
      onAdded: () => {
        setDisabled(false);
      },
    });
  }, [
    isReady,
    productDetails,
    checkedInventory,
    soldOut,
    selectedAddons,
    selectedQty,
    selectedModifiersList,
    dispatch,
  ]);

  useEffect(() => {
    if (isReady && wasAddedBeforeReadyRef.current) {
      wasAddedBeforeReadyRef.current = false;
      addToCartHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]);

  const triggerAddToCart = () => {
    if (disabled) return;

    if (!isReady) {
      setDisabled(true);
      wasAddedBeforeReadyRef.current = true;
      return;
    }

    addToCartHandler();
  };

  return { isReady, disabled, triggerAddToCart };
};
